import React, { useEffect, useRef, useState } from "react";

const AnimatedCircle = ({
  percentage,
  size = 96,
  strokeWidth = 8,
  strokeColor = "#00cfff", // bright cyan for loading
  trailColor = "#e0e0e0",
  textColor = "#000",
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const [progress, setProgress] = useState(0);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  // Observe if the component is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  // Animate loading loop
  useEffect(() => {
    if (!inView) return;

    let isCancelled = false;

    const animate = () => {
      let current = 0;
      const duration = 1500;
      const intervalTime = 15;
      const totalSteps = duration / intervalTime;
      const increment = percentage / totalSteps;

      const interval = setInterval(() => {
        if (isCancelled) return;

        current += increment;
        if (current >= percentage) {
          current = percentage;
          setProgress(current);
          clearInterval(interval); // Stop the interval when it reaches 100%

        } else {
          setProgress(current);
        }
      }, intervalTime);
    };

    animate();

    return () => {
      isCancelled = true;
    };
  }, [inView, percentage]);

  return (
    <div ref={ref} style={{ width: size, height: size }} className="relative">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={trailColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Animated Stroke */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress / 100)}
          strokeLinecap="round"
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center font-bold"
        style={{ color: textColor, fontSize: size * 0.25 }}
      >
        {Math.round(progress)}%
      </div>
    </div>
  );
};

export default AnimatedCircle;
